import { Link } from "react-router-dom";
import { FadeUp } from "@/components/Reveal";
import { ASSETS } from "@/data/site";

export default function About() {
  return (
    <section id="about" className="bg-espresso py-24 text-ivory lg:py-32" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="relative lg:col-span-5">
          <FadeUp>
            <div className="overflow-hidden rounded-3xl">
              <img
                src={ASSETS.portrait}
                alt="Athena — founder of inbornbirth"
                className="aspect-[4/5] w-full object-cover"
                data-testid="about-portrait"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.2} className="absolute -right-4 -bottom-6 lg:-right-10 lg:-bottom-10">
            <div className="h-28 w-28 overflow-hidden rounded-full ring-4 ring-espresso lg:h-36 lg:w-36" data-testid="about-badge">
              <img src={ASSETS.badge} alt="Certified practitioner seal" className="h-full w-full object-cover" />
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-mauve">About your practitioner</p>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight sm:text-5xl lg:text-6xl">
              Meet <span className="italic text-mauve">Athena</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-8 space-y-6 text-base leading-relaxed text-ivory/75">
              <blockquote className="border-l-2 border-mauve/70 pl-6 font-serif text-xl italic leading-snug text-ivory/90 sm:text-2xl">
                "My work is deeply inspired by the old understanding that when a
                baby is born, a mother is being born too — and both deserve to
                be received with tenderness."
              </blockquote>
              <p>
                I believe the postpartum season is not something a family should
                simply hope to find their way through once the baby arrives.
                Traditionally, there was preparation. Woman learned from woman.
                Families gathered wisdom ahead of time. Mothers were taught what
                their bodies would need, partners were shown how to protect the
                mother's rest, and the home was prepared to become a place of
                warmth, nourishment and recovery alongside the family and village.
              </p>
              <p className="font-serif text-xl italic text-mauve">
                To me, education is the first form of care.
              </p>
              <p>
                It is the gathering around the table before the journey begins.
                It gives families language, understanding, and permission to
                approach postpartum differently — to see this time not as
                something to hurry through but as a sacred season worthy of
                protection.
              </p>
              <p>
                From there, my work can move from teaching to companionship.
              </p>
              <p>
                As a postpartum companion I come alongside the mother in the
                days and weeks after birth with the kind of practical and
                nurturing care women have offered one another for generations.
              </p>
              <p>
                Whether I first meet a family across a classroom table or enter
                their home during the tender weeks after birth, my hope is the same —
              </p>
              <ul className="space-y-2 border-y border-ivory/15 py-6 font-serif text-lg italic text-ivory/90">
                <li>That the mother feels <span className="text-mauve">remembered</span>.</li>
                <li>That the partner feels <span className="text-mauve">prepared</span>.</li>
                <li>That the family feels <span className="text-mauve">supported</span>.</li>
              </ul>
              <p>
                And that the postpartum season becomes less about simply
                getting through the days and more about being nourished,
                protected, and gathered into a new beginning.
              </p>
              <p>
                This is the heart of my work — education first, companionship
                after, and throughout it all, the remembrance that mothers
                were never meant to do this alone.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.25}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="about-book-button"
                className="rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark"
              >
                Book with Athena
              </Link>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-ivory/50">
                Innate Postpartum Care · Certified Practitioner
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
